import type { DomSanitizer } from '@angular/platform-browser';

import { pipe } from 'rxjs';

import {
  ReportsTranslations,
  DatasetBase,
  MAX_SECOND_DIMENSION_LENGTH,
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewData,
  ReportDashboardComponentViewMode,
  ReportDashboardMetricOptions,
  ReportsBarChartOptions,
  ReportsEChartsOption,
  ReportsPieChartOptions,
  VisualizationMapperOperator,
  Blueprint,
} from '@core/reports-types';
import { pipeCompose } from '@core/utils-legacy';

import { pivotTable, summary, table } from './generic';
import { breakdownTooltipContextFactory } from './strategies/breakdown/tooltip';
import { addBreakdownBarSeries, addBreakdownPieSeries } from './strategies/composables';
import { addLegendNameFormatting } from './strategies/composables/legend';
import {
  addXAxisDurationLabelFactory,
  createXAxisTypeValue,
} from './strategies/config-factories/x-axis';
import {
  addYAxisCategoryTruncate,
  createYAxisCategoryFactory,
} from './strategies/config-factories/y-axis';
import { dataZoom } from './strategies/datazoom';
import { getXAxisNameLabel } from './strategies/get-x-axis-name-label';
import { filterByMetricKind, fromBlueprints } from './strategies/operators';
import { filterDatasetForPieChart } from './strategies/pie-dataset';
import { xAxisMediaQueries } from './strategies/x-axis-media-queries';

const barChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
): ReportsBarChartOptions<'value', 'category'> => {
  const dataZoomCmp = dataZoom(component, dataset.getRawData());

  const { primaryGroup, secondaryGroup } = component;
  const stack = primaryGroup ?? secondaryGroup ?? undefined;

  const xAxis = pipeCompose(
    createXAxisTypeValue,
    addXAxisDurationLabelFactory(dataset, translations.i18n),
  );

  const yAxis = pipeCompose(createYAxisCategoryFactory(dataset), addYAxisCategoryTruncate);

  return pipeCompose(
    () =>
      ({
        graphic: [getXAxisNameLabel(metric.xAxis)],
        xAxis,
        yAxis,
        dataZoom: dataZoomCmp,
        dataset: dataset.dataset,
        tooltip: {
          show: true,
          trigger: 'axis',
          axisPointer: {
            type: 'none',
          },
          formatter: (params: any) =>
            <any>breakdownTooltipContextFactory(dataset, translations, <any>params, false),
        },
        media: [...xAxisMediaQueries(metric)],
      }) satisfies ReportsEChartsOption,
    addBreakdownBarSeries(dataset, translations.i18n, stack),
  );
};

const pieChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
): ReportsPieChartOptions =>
  pipeCompose(
    () => ({
      dataset: dataset.dataset,
      legend: {},
    }),
    addLegendNameFormatting(dataset),
    opts => ({
      ...opts,
      tooltip: {
        show: true,
        trigger: <const>'item',
        formatter: (params: any) =>
          breakdownTooltipContextFactory(dataset, translations, params, true),
      },
    }),
    addBreakdownPieSeries(dataset, translations.i18n),
  );

export const breakdownBlueprints = (
  sanitizer: DomSanitizer,
  translations: ReportsTranslations,
): Array<Blueprint<any>> => [
  table(translations.i18n, ReportDashboardComponentKind.Breakdown),
  pivotTable(translations, ReportDashboardComponentKind.Breakdown),
  summary(translations.i18n, ReportDashboardComponentKind.Breakdown),
  {
    match: {
      kind: ReportDashboardComponentKind.Breakdown,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Bar,
    },
    factory: ({ component, dataset: originalDataset, metric, meta }) => {
      let dataset = originalDataset;
      if (meta.truncateDataset) {
        const truncateLength =
          typeof meta.truncateDataset === 'number'
            ? meta.truncateDataset
            : meta.truncateDataset(component, dataset);
        dataset = dataset.truncateDataset('first', truncateLength);
      }
      if (dataset.dimensions.length > 3) {
        dataset = dataset.truncateDataset(
          'second',
          MAX_SECOND_DIMENSION_LENGTH,
          true,
          translations.i18n.translate('tlr.group_name.other'),
        );
      }
      return {
        vis: barChart(component, dataset, metric, translations),
        dataset: dataset,
      };
    },
  },
  {
    match: {
      kind: ReportDashboardComponentKind.Breakdown,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Pie,
    },
    factory: ({ component, dataset, metric }) => {
      const filteredDataset = filterDatasetForPieChart(dataset);
      return {
        vis: pieChart(component, filteredDataset, metric, translations),
        dataset: filteredDataset,
      };
    },
  },
];

export const breakdown = (
  sanitizer: DomSanitizer,
  translations: ReportsTranslations,
): VisualizationMapperOperator =>
  pipe(
    filterByMetricKind(ReportDashboardComponentKind.Breakdown),
    fromBlueprints(breakdownBlueprints(sanitizer, translations)),
  );
